import { get, writable } from "svelte/store";
import API from "../../API/API";
import LoadState from "../../Enums/LoadState";
import GeoHelper from "../../Helpers/GeoHelper";
import MapDataSource from "../../Map/Enums/MapDataSource";
import MapLayer from "../../Map/Enums/MapLayer";
import MapLayerGroup from "../../Map/Enums/MapLayerGroup";
import MapLayerManager from "../../Map/MapLayerManager";
import { Layout, Paint } from "../../Map/MapLayerStyles";
import PopupStore from "../../Map/Popup/PopupStore";
import MapStore from "../../Map/Store";

const SatRFStore = {
	meta: {
		loadState: writable(LoadState.INIT),
		error: writable(""),
		opacity: writable(1.0),
		visible: writable(false),
		mapLayersInitted: writable(false)
	},
	results: {
		featuresTrails: writable([]),
		featuresEllipses: writable([])
	},
	actions: {
		initMap: (map) => {
			if (get(SatRFStore.meta.mapLayersInitted)) {
				return;
			}
			map.addSource(MapDataSource.CLEOS_SATRF_VESSELS, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addSource(MapDataSource.CLEOS_SATRF, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});

			map.addLayer({
				id: MapLayer.CLEOS_SATRF,
				source: MapDataSource.CLEOS_SATRF,
				type: "fill",
				paint: Paint.rfVesselPolys,
				layout: {
					visibility: "visible"
				}
			});

			map.addLayer({
				id: MapLayer.CLEOS_SATRF_VESSELS,
				type: "symbol",
				source: MapDataSource.CLEOS_SATRF_VESSELS,
				layout: Layout.rfVesselPoints,
				paint: {
					"icon-opacity": 1.0
					// [
					// 	"interpolate",
					// 	["linear"],
					// 	["zoom"],
					// 	7, 1, 	// at Zoom level 7, it's full transparent.
					// 	23, 0.5 // at Zoom level 9, it's half opaque.
					// ]
				}
			});

			map.setLayerZoomRange(MapLayer.CLEOS_SATRF, 0, 24);
			map.setLayerZoomRange(MapLayer.CLEOS_SATRF_VESSELS, 0, 24);

			MapLayerManager.addLayerGroup(
				MapLayerGroup.CLEOS_SATRF,
				[MapLayer.CLEOS_SATRF, MapLayer.CLEOS_SATRF_VESSELS],
				[MapDataSource.CLEOS_SATRF_VESSELS, MapDataSource.CLEOS_SATRF]
			);

			const onEnterPoly = (e) => {
				if (e.features.length > 0) {
					const props = e.features[0].properties;
					const coords = [e.features[0].properties.longitude, e.features[0].properties.latitude];
					PopupStore.actions.custom(
						{
							MMSI: props.mmsi,
							Frequency: props.frequency,
							errorEllipseMinor: props.errorEllipseMinor,
							errorEllipseMajor: props.errorEllipseMajor,
							errorEllipseRotation: props.errorEllipseRotation,
							Timestamp: props.timestamp
						},
						coords,
						true
					);
				}
			};
			const onLeavePoly = (e) => {
				PopupStore.actions.custom({}, null, false);
			};
			const onEnterPoint = (e) => {
				if (e.features.length > 0) {
					const props = e.features[0].properties;
					const coords = e.features[0].geometry.coordinates;
					PopupStore.actions.custom(
						{
							MMSI: props.mmsi,
							Flag: props.flag_short_code + " (" + props.flag + ")",
							Speed: props.speed + " Knots",
							Timestamp: props.timestamp
						},
						coords,
						true
					);
				}
			};
			const onLeavePoint = (e) => {
				PopupStore.actions.custom({}, null, false);
			};

			map.on("mouseenter", MapLayer.CLEOS_SATRF_VESSELS, onEnterPoint);
			map.on("mousemove", MapLayer.CLEOS_SATRF_VESSELS, onEnterPoint);
			map.on("mouseleave", MapLayer.CLEOS_SATRF_VESSELS, onLeavePoint);

			map.on("mouseenter", MapLayer.CLEOS_SATRF, onEnterPoly);
			map.on("mousemove", MapLayer.CLEOS_SATRF, onEnterPoly);
			map.on("mouseleave", MapLayer.CLEOS_SATRF, onLeavePoly);

			SatRFStore.meta.mapLayersInitted.set(true);
		},
		deinitMap: () => {
			if (!get(SatRFStore.meta.mapLayersInitted)) {
				return;
			}
			MapLayerManager.removeLayerGroup(MapLayerGroup.CLEOS_SATRF);
			SatRFStore.meta.mapLayersInitted.set(false);
		},
		updateMapData: () => {
			const map = MapStore.helpers.getMap();

			const featuresEllipses = get(SatRFStore.results.featuresEllipses);
			if (map.getSource(MapDataSource.CLEOS_SATRF)) {
				map.getSource(MapDataSource.CLEOS_SATRF).setData(featuresEllipses);
			}
			const featuresTrails = get(SatRFStore.results.featuresTrails);
			if (map.getSource(MapDataSource.CLEOS_SATRF_VESSELS)) {
				map.getSource(MapDataSource.CLEOS_SATRF_VESSELS).setData(featuresTrails);
			}
		},
		init: () => {
			// Already initted
			if (get(SatRFStore.meta.loadState) !== LoadState.INIT) {
				SatRFStore.actions.updateMapData();
				return;
			}

			SatRFStore.meta.error.set("");
			SatRFStore.meta.loadState.set(LoadState.LOADING);

			Promise.all([
				API.getDatasetParsedJSON("cleos_satellite_rf_vessels"),
				API.getDatasetParsedJSON("cleos_satellite_rf")
			])
				.then((responses) => {
					const dataVessels = responses[0];
					const dataGeom = responses[1];

					SatRFStore.results.featuresTrails.set(dataVessels);
					SatRFStore.results.featuresEllipses.set(dataGeom);

					SatRFStore.actions.updateMapData();

					SatRFStore.meta.loadState.set(LoadState.LOADED);

					MapLayerManager.updateVisibleLayers();
				})
				.catch((err) => {
					console.error(err);
					SatRFStore.meta.loadState.set(LoadState.INIT);
					SatRFStore.meta.error.set(err);
				});
		}
	}
};

export default SatRFStore;
